/**
 * Utility functions for deterministic template placeholder assignment
 */

/**
 * Generates a consistent placeholder class based on the template name.
 * Uses a simple hash function to deterministically assign one of 6 placeholder designs.
 * The same template name will always get the same placeholder design.
 * 
 * @param templateName - The name of the template
 * @param index - Optional index to use as fallback if templateName is empty
 * @returns The placeholder class name (e.g., 'placeholder-1', 'placeholder-2', etc.)
 */
export function getTemplatePlaceholderClass(templateName: string, index?: number): string {
	// If no template name, use index as fallback
	if (!templateName && index !== undefined) {
		return `placeholder-${(index % 6) + 1}`;
	}

	// Use djb2 hash algorithm for consistent distribution
	const hash = djb2Hash(templateName || '');
	
	// Map hash to one of 6 placeholder designs (1-6)
	const placeholderNumber = (Math.abs(hash) % 6) + 1;
	
	return `placeholder-${placeholderNumber}`;
}

/**
 * djb2 hash algorithm - simple and effective for string hashing
 * Provides good distribution for our use case
 * 
 * @param str - The string to hash
 * @returns A numeric hash value
 */
function djb2Hash(str: string): number {
	let hash = 5381;
	
	for (let i = 0; i < str.length; i++) {
		const char = str.charCodeAt(i);
		hash = ((hash << 5) + hash) + char; // hash * 33 + char
	}
	
	return hash;
}

/**
 * Helper function to get placeholder variant number directly
 * Useful for cases where you need the number instead of the class name
 * 
 * @param templateName - The name of the template
 * @param index - Optional index to use as fallback if templateName is empty
 * @returns The placeholder variant number (1-6)
 */
export function getTemplatePlaceholderVariant(templateName: string, index?: number): number {
	if (!templateName && index !== undefined) {
		return (index % 6) + 1;
	}
	
	const hash = djb2Hash(templateName || '');
	return (Math.abs(hash) % 6) + 1;
}

/**
 * Test function to verify distribution of placeholder assignments
 * Useful for debugging and ensuring even distribution
 * 
 * @param sampleNames - Array of template names to test
 * @returns Object with distribution statistics
 */
export function testPlaceholderDistribution(sampleNames: string[]): { [key: string]: number } {
	const distribution: { [key: string]: number } = {
		'placeholder-1': 0,
		'placeholder-2': 0,
		'placeholder-3': 0,
		'placeholder-4': 0,
		'placeholder-5': 0,
		'placeholder-6': 0
	};
	
	sampleNames.forEach(name => {
		const className = getTemplatePlaceholderClass(name);
		distribution[className]++;
	});
	
	return distribution;
}

/**
 * Interface for template with placeholder support
 */
export interface TemplateWithPlaceholder {
	name: string;
	previewUrl?: string;
	placeholderClass?: string;
}

/**
 * Utility to assign placeholder classes to a collection of templates
 * 
 * @param templates - Array of templates
 * @returns Templates with assigned placeholder classes
 */
export function assignPlaceholderClasses<T extends TemplateWithPlaceholder>(templates: T[]): T[] {
	return templates.map((template, index) => ({
		...template,
		placeholderClass: !template.previewUrl 
			? getTemplatePlaceholderClass(template.name, index)
			: undefined
	}));
}